import { CheckIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType } from 'actions'
import type { InputType } from 'config'
import { Fragment, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { commitConditionImport } from 'services/apis/conditions'
import { Button, Modal, Tooltip } from 'stories/components'
import { getOptionValues } from 'utils/general'
import { RenderInput } from 'utils/RenderInput'

import { conditionInputs } from '../constant'
import type { ICondition } from './types'

interface ConditionImportDraft {
  rowIndex: number
  data: Record<string, any>
  missingFields: string[]
  errors: string[]
  generatedNo: boolean
  templates: number[]
  selected?: boolean
  existingConditionId?: number
}

interface ConditionImportModalProps {
  rows: ConditionImportDraft[]
  conditions: ICondition[]
  categories: string[]
  onClose: () => void
  onComplete: () => void
}

const importInputKeys = [
  'displayNo',
  'name',
  'category',
  'type',
  'responsibility',
  'intext',
  'documentCategory',
  'description',
  'brokerDescription',
  'borrowerName',
  'borrowerDescription',
  'requested',
  'requestedTo',
  'products',
  'transactions',
]

const selectValidationKeys = ['category', 'type', 'responsibility', 'documentCategory']

const CONDITION_NO_EXISTS_MSG =
  'Condition Number already exists. Update your excel sheet and reimport, or click the edit icon on the condition line to change the name or number.'
const CONDITION_NAME_EXISTS_MSG =
  'Condition Name already exists. Update your excel sheet and reimport, or click the edit icon on the condition line to change the name or number.'

const STALE_EXISTENCE_ERRORS = [
  'No already exists.',
  'Name already exists.',
  CONDITION_NO_EXISTS_MSG,
  CONDITION_NAME_EXISTS_MSG,
]

/** Multiselect options are either a list of values or a map of value to label. */
const optionValues = (options: unknown): string[] => {
  if (Array.isArray(options)) return options.map((item) => `${item}`)
  if (options && typeof options === 'object') return Object.keys(options as Record<string, unknown>)
  return []
}

const selectedOptionKeys = (value: unknown): string[] => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return []
  const selected = value as Record<string, boolean>
  return Object.keys(selected).filter((key) => selected[key])
}

const isSameConditionUpdate = (draft: ConditionImportDraft, conditions: ICondition[]) => {
  const displayNo = Number(draft.data.displayNo)
  const name = `${draft.data.name || ''}`.trim().toLowerCase()
  const matchByNo = displayNo ? conditions.find((c) => c.displayNo === displayNo) : undefined
  const matchByName = name ? conditions.find((c) => c.name.trim().toLowerCase() === name) : undefined
  return !!(matchByNo && matchByName && matchByNo.id === matchByName.id)
}

export function ConditionImportModal({ rows, conditions, categories, onClose, onComplete }: ConditionImportModalProps) {
  const [conditionTypeOptions, documentCategoryOptions] = useSelector((state: any) => {
    return [
      state.globalConfigReducer[GlobalConfigType.ConditionTypes],
      state.globalConfigReducer[GlobalConfigType.DocumentCategory],
    ]
  })
  const [drafts, setDrafts] = useState<ConditionImportDraft[]>(
    rows.map((row) => {
      const isUpdate = !!row.existingConditionId || isSameConditionUpdate(row, conditions)
      const blockingErrors = (row.errors || []).filter((error) => !STALE_EXISTENCE_ERRORS.includes(error))
      const displayNo = Number(row.data.displayNo)
      const name = `${row.data.name || ''}`.trim().toLowerCase()
      const hasNoConflict = !isUpdate && !!displayNo && conditions.some((c) => c.displayNo === displayNo)
      const hasNameConflict = !isUpdate && !!name && conditions.some((c) => c.name.trim().toLowerCase() === name)
      return {
        ...row,
        selected: !blockingErrors.length && !row.missingFields.length && !hasNoConflict && !hasNameConflict,
      }
    }),
  )
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())
  const [isLoading, setIsLoading] = useState(false)
  const inputs = useMemo(() => {
    const baseInputs = conditionInputs(categories)
    baseInputs.type.options = getOptionValues(conditionTypeOptions)
    baseInputs.documentCategory.options = getOptionValues(documentCategoryOptions)
    return baseInputs
  }, [categories, conditionTypeOptions, documentCategoryOptions])

  const requiredFields = useMemo(() => {
    return importInputKeys
      .map((key) => ({ key, input: inputs[key] }))
      .filter(({ input }) => input?.required)
      .map(({ key, input }) => ({ key, title: input.title || key }))
  }, [inputs])

  const existingByDisplayNo = useMemo(() => {
    const map = new Map<number, ICondition>()
    conditions.forEach((condition) => {
      if (condition.displayNo) map.set(condition.displayNo, condition)
    })
    return map
  }, [conditions])

  const existingByName = useMemo(() => {
    const map = new Map<string, ICondition>()
    conditions.forEach((condition) => {
      const name = condition.name.trim().toLowerCase()
      if (name) map.set(name, condition)
    })
    return map
  }, [conditions])

  const setDraft = (index: number, draft: ConditionImportDraft) => {
    setDrafts((prev) => prev.map((item, itemIndex) => (itemIndex === index ? draft : item)))
  }

  const updateField = (index: number, key: string, value: any) => {
    const draft = { ...drafts[index], data: { ...drafts[index].data, [key]: value }, errors: [] }
    setDraft(index, draft)
  }

  const toggleExpandedRow = (rowIndex: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(rowIndex)) next.delete(rowIndex)
      else next.add(rowIndex)
      return next
    })
  }

  const getMatchInfo = (draft: ConditionImportDraft) => {
    const displayNo = Number(draft.data.displayNo)
    const name = `${draft.data.name || ''}`.trim().toLowerCase()
    const matchByNo = displayNo ? existingByDisplayNo.get(displayNo) : undefined
    const matchByName = name ? existingByName.get(name) : undefined
    const isUpdate = !!(matchByNo && matchByName && matchByNo.id === matchByName.id)
    return { displayNo, name, matchByNo, matchByName, isUpdate, existingConditionId: isUpdate ? matchByNo!.id : undefined }
  }

  const getDraftErrors = (draft: ConditionImportDraft, index: number) => {
    const errors = new Set<string>(draft.errors.filter((error) => !STALE_EXISTENCE_ERRORS.includes(error)))
    const addError = (message: string) => errors.add(message)

    requiredFields.forEach((field) => {
      const value = draft.data[field.key]
      if (value === undefined || value === null || value === '') addError(`${field.title} is required.`)
    })

    const { displayNo, name, matchByNo, matchByName, isUpdate } = getMatchInfo(draft)
    if (!displayNo || !Number.isInteger(displayNo) || displayNo <= 0) addError('No must be a number.')

    if (!isUpdate) {
      if (matchByNo) addError(CONDITION_NO_EXISTS_MSG)
      if (matchByName) addError(CONDITION_NAME_EXISTS_MSG)
    }

    if (
      displayNo &&
      drafts.some((item, itemIndex) => itemIndex !== index && Number(item.data.displayNo) === displayNo)
    ) {
      addError('No is duplicated in the import file.')
    }

    if (
      name &&
      drafts.some((item, itemIndex) => itemIndex !== index && `${item.data.name || ''}`.trim().toLowerCase() === name)
    ) {
      addError('Name is duplicated in the import file.')
    }

    selectValidationKeys.forEach((key) => {
      const value = `${draft.data[key] || ''}`.trim()
      const options = optionValues(inputs[key]?.options)
      if (value && options.length && !options.includes(value)) {
        addError(`${inputs[key]?.title || key} contains invalid value: ${value}.`)
      }
    })

    importInputKeys
      .filter((key) => inputs[key]?.inputType === 'multiselect')
      .forEach((key) => {
        const selectedValues = selectedOptionKeys(draft.data[key])
        const options = optionValues(inputs[key].options)
        const invalidValues = selectedValues.filter((item) => !options.includes(item))
        if (invalidValues.length) addError(`${inputs[key].title} contains invalid values: ${invalidValues.join(', ')}.`)
      })

    return Array.from(errors)
  }

  const isDraftValid = (draft: ConditionImportDraft, index: number) => getDraftErrors(draft, index).length === 0

  const commitDrafts = async (targetDrafts: ConditionImportDraft[]) => {
    if (!targetDrafts.length) return

    setIsLoading(true)
    try {
      const res = await commitConditionImport(
        targetDrafts.map((draft) => {
          const { existingConditionId } = getMatchInfo(draft)
          return {
            rowIndex: draft.rowIndex,
            data: draft.data,
            templates: draft.templates,
            existingConditionId: existingConditionId || draft.existingConditionId,
          }
        }),
      )

      const failedRows = (res.results || []).filter((result: any) => !result.success)
      const successfulRows = new Set(
        (res.results || []).filter((result: any) => result.success).map((result: any) => result.rowIndex),
      )
      setDrafts((prev) =>
        prev
          .filter((draft) => !successfulRows.has(draft.rowIndex))
          .map((draft) => {
            const failedRow = failedRows.find((result: any) => result.rowIndex === draft.rowIndex)
            return failedRow ? { ...draft, errors: [failedRow.message] } : draft
          }),
      )

      toast(res.message || 'Condition import completed.', { type: failedRows.length ? 'warning' : 'success' })
      onComplete()
      if (!failedRows.length) onClose()
    } finally {
      setIsLoading(false)
    }
  }

  const validSelectedDrafts = drafts.filter((draft, index) => draft.selected && isDraftValid(draft, index))

  return (
    <Modal isOpen title="Import Conditions" titleOkay="" titleCancel="Close" onClose={onClose} loading={isLoading}>
      <div className="w-[1200px] max-w-full">
        <div className="flex justify-between items-center mb-4">
          <p className="text-sm text-gray-600">
            Complete missing fields, choose templates, then import each condition or all valid selected conditions.
            Matching No + Name updates the existing condition.
          </p>
          <Button disabled={!validSelectedDrafts.length || isLoading} onClick={() => commitDrafts(validSelectedDrafts)}>
            Import All Valid
          </Button>
        </div>
        <div className="overflow-auto max-h-[70vh]">
          <table className="w-full text-sm text-left">
            <thead className="text-xs uppercase bg-gray-50 sticky top-0">
              <tr>
                <th className="px-2 py-2">Use</th>
                <th className="px-2 py-2">Row</th>
                <th className="px-2 py-2">No</th>
                <th className="px-2 py-2">Name</th>
                <th className="px-2 py-2">Category</th>
                <th className="px-2 py-2">Type</th>
                <th className="px-2 py-2">Status</th>
                <th className="px-2 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {drafts.map((draft, index) => {
                const errors = getDraftErrors(draft, index)
                const isValid = errors.length === 0
                const isExpanded = expandedRows.has(draft.rowIndex)
                const { isUpdate } = getMatchInfo(draft)
                return (
                  <Fragment key={draft.rowIndex}>
                    <tr className={`border-b align-top ${isValid ? 'bg-white' : 'bg-red-50'}`}>
                      <td className="px-2 py-2">
                        <input
                          type="checkbox"
                          checked={!!draft.selected}
                          onChange={(event) => setDraft(index, { ...draft, selected: event.target.checked })}
                        />
                      </td>
                      <td className="px-2 py-2">
                        {draft.rowIndex}
                        {draft.generatedNo && <div className="text-xs text-shade-blue">No generated</div>}
                      </td>
                      <td className="px-2 py-2">{draft.data.displayNo}</td>
                      <td className="px-2 py-2">{draft.data.name}</td>
                      <td className="px-2 py-2">{draft.data.category}</td>
                      <td className="px-2 py-2">{draft.data.type}</td>
                      <td className="px-2 py-2">
                        {isValid ? (
                          <span className={isUpdate ? 'text-amber-700' : 'text-green-700'}>
                            {isUpdate ? 'Ready (update)' : 'Ready'}
                          </span>
                        ) : (
                          <ul className="text-red-700 list-disc pl-4">
                            {errors.map((error, errorIndex) => (
                              <li key={errorIndex}>{error}</li>
                            ))}
                          </ul>
                        )}
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex items-start gap-2">
                          <Tooltip message={isExpanded ? 'Close Edit' : 'Edit'} placement="right">
                            <button
                              aria-label={isExpanded ? 'Close edit' : 'Edit condition'}
                              className="btn-icon mt-0.5"
                              type="button"
                              onClick={() => toggleExpandedRow(draft.rowIndex)}
                            >
                              <PencilSquareIcon className="w-4 h-4 text-shade-blue" />
                            </button>
                          </Tooltip>
                          <Tooltip message={isUpdate ? 'Update Condition' : 'Import Condition'} placement="right">
                            <button
                              aria-label={isUpdate ? 'Update condition' : 'Import condition'}
                              className={`btn-icon ${!isValid || isLoading ? 'cursor-not-allowed opacity-50' : ''}`}
                              disabled={!isValid || isLoading}
                              type="button"
                              onClick={() => commitDrafts([draft])}
                            >
                              <CheckIcon className="w-4 h-4" />
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className={`border-b ${isValid ? 'bg-white' : 'bg-red-50'}`}>
                        <td className="px-2 py-3" colSpan={8}>
                          <div className="grid grid-cols-2 gap-2">
                            {importInputKeys.map((key) => {
                              const input = {
                                ...inputs[key],
                                value: draft.data[key],
                                error: errors.find((error) => error.startsWith(`${inputs[key].title}`)) || '',
                              } as InputType
                              return (
                                <div className={`input ${input.span ? `md:col-span-${input.span}` : ''}`} key={key}>
                                  <RenderInput
                                    input={input}
                                    Key={key}
                                    onChange={(_key: string, value: any) => updateField(index, key, value)}
                                  />
                                </div>
                              )
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  )
}
